import Vue from "vue";
import axios from "axios";

class ApiClient {
  constructor(url) {
    this._url = url;
    // 強制綁定所有方法
    for (const key of Object.getOwnPropertyNames(ApiClient.prototype)) {
      if (typeof this[key] === "function") {
        this[key] = this[key].bind(this);
      }
    }
  }

  async _doRequestWithRetry(config, { retry = 1, isSuccess = (res) => res?.status === 200 } = {}) {
    let lastError;

    for (let attempt = 0; attempt <= retry; attempt++) {
      try {
        const res = await axios(config);
        const ok = typeof isSuccess === "function" ? isSuccess(res) : res.status === 200;
        if (ok) return res.data;

        lastError = res;
        if (this._isNonRetryableError(res)) throw res;
      } catch (err) {
        lastError = err;
        if (this._isNonRetryableError(err)) throw err;
        console.log(`Attempt ${attempt + 1} failed`, err);
      }
    }

    throw lastError;
  }

  _isNonRetryableError(err) {
    const status = this.getErrorStatus(err);
    return this.isTSMReauthRequired(err) || (status >= 400 && status < 500);
  }

  getErrorData(err) {
    return err?.response?.data || err?.data || {};
  }

  getErrorStatus(err) {
    return err?.response?.status || err?.status || 0;
  }

  getErrorCode(err) {
    const data = this.getErrorData(err);
    return data?.Code || data?.code || "";
  }

  isNetworkError(err) {
    return !!(
      err &&
      !err.response &&
      (err.isAxiosError ||
        err.request ||
        err.code === "ERR_NETWORK" ||
        err.code === "ECONNABORTED" ||
        err.message === "Network Error" ||
        /timeout/i.test(err.message || ""))
    );
  }

  isTSMReauthRequired(err) {
    return this.getErrorStatus(err) === 401 && this.getErrorCode(err) === "reauth_required";
  }

  getTSMReauthScope(err) {
    const data = this.getErrorData(err);
    return data?.Scope || "";
  }

  _requestLog(method, address, payload, options) {
    if (process.env.NODE_ENV === "development") {
      console.log("[API Request]", {
        path: "[" + method.toUpperCase() + "] " + address,
        payload,
        retry: options.retry || 0,
        isSuccessCheck: typeof options.isSuccess === "function",
      });
    }
  }

  _responseLog(method, address, response) {
    if (process.env.NODE_ENV === "development") {
      console.log("[API Response]", {
        path: "[" + method.toUpperCase() + "] " + address,
        response: response,
      });
    }
  }

  async _get(address, options = {}) {
    this._requestLog("get", address, null, options);
    const { timeout, ...retryOptions } = options || {};
    const config = { method: "get", url: `${this._url}${address}` };
    if (Number.isFinite(timeout) && timeout > 0) config.timeout = timeout;
    const response = await this._doRequestWithRetry(config, retryOptions);
    this._responseLog("get", address, response);
    return response;
  }

  async _post(address, payload, options = {}) {
    this._requestLog("post", address, payload, options);
    const { timeout, ...retryOptions } = options || {};
    const config = { method: "post", url: `${this._url}${address}`, data: payload };
    if (Number.isFinite(timeout) && timeout > 0) config.timeout = timeout;
    const response = await this._doRequestWithRetry(config, retryOptions);
    this._responseLog("post", address, response);
    return response;
  }

  async _put(address, payload, options = {}) {
    this._requestLog("put", address, payload, options);
    const response = await this._doRequestWithRetry({ method: "put", url: `${this._url}${address}`, data: payload }, options);
    this._responseLog("put", address, response);
    return response;
  }

  async _delete(address, options = {}) {
    // 取出 options 內的 data 並 propagate 到 axios config，避免 caller {...options, data} 形式被靜默吞掉
    const { data, ...rest } = options || {};
    const config = { method: "delete", url: `${this._url}${address}` };
    if (data !== undefined) config.data = data;
    this._requestLog("delete", address, data ?? null, rest);
    const response = await this._doRequestWithRetry(config, rest);
    this._responseLog("delete", address, response);
    return response;
  }

  async _patch(address, payload, options = {}) {
    this._requestLog("patch", address, payload, options);
    const response = await this._doRequestWithRetry({ method: "patch", url: `${this._url}${address}`, data: payload }, options);
    this._responseLog("patch", address, response);
    return response;
  }

  throwApiError(err) {
    // 取得錯誤訊息（支援多種錯誤結構）
    // axios interceptor 目前 Promise.resolve(err.response) 把 error 轉成 response 物件 (E058 anti-pattern),
    // 所以這邊既要支援標準 axios error (err.response.data), 也要支援降級後的 response object (err.data)
    const data = this.getErrorData(err);
    // 兼容 backend lowercase `error` 與新規範 PascalCase `Error` (feedback_api_response_pascal_case)
    const errorMsg = data?.error || data?.Error;
    const status = this.getErrorStatus(err);
    const code = this.getErrorCode(err);

    // 社區停用優先顯示具體原因，避免落入一般登入或門禁提示。
    if (code === "community_disabled") {
      Vue.swal({ icon: "warning", title: "社區已停用", text: "此社區目前暫停使用，請聯繫建商或系統管理員。" });
      return;
    }

    // TSM 7 個業務 Code 優先處理 (固定中文文案 + 操作提示, 不走通用 errorMsg)
    const tsmText = code ? this._translateTSMCode(code, data) : null;
    if (tsmText) {
      Vue.swal({ icon: "warning", title: tsmText.title, text: tsmText.hint });
      return;
    }

    if (status === 403) {
      Vue.swal({ icon: "error", text: "無操作權限" });
    } else if (status === 429) {
      const retryAfter = data?.retryAfter;
      const text = retryAfter ? `操作過於頻繁，請在 ${retryAfter} 秒後再試` : "操作過於頻繁，請稍後再試";
      Vue.swal({ icon: "error", text });
    } else if (errorMsg) {
      // 後端返回的錯誤訊息
      Vue.swal({ icon: "error", text: this._translateError(errorMsg) });
    } else if (code) {
      const fallback = this._translateTSMCodeFallback(code, status);
      Vue.swal({ icon: fallback.icon, title: fallback.title, text: fallback.hint });
    } else if (status) {
      const fallback = this._translateHTTPStatusFallback(status);
      Vue.swal({ icon: fallback.icon, title: fallback.title, text: fallback.hint });
    } else if (this.isNetworkError(err)) {
      const timeout = err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
      Vue.swal({ icon: "error", text: timeout ? "網路連線逾時，請確認連線後再試" : "網路連線異常，請確認連線後再試" });
    } else if (err?.message && !err?.response) {
      // 純 Error (frontend validation throw, 非網路錯誤) 直接顯示 message; 例如 buildPayload throw 的「每日最多 7 個時段」
      Vue.swal({ icon: "error", text: err.message });
    } else if (err) {
      console.log("throwError: ->", err);
      Vue.swal({ icon: "error", text: "網路連線異常" });
    }
  }

  _translateError(errorMsg) {
    const tsmRecoverError = this._translateRawTSMRecoverError(errorMsg);
    if (tsmRecoverError) return tsmRecoverError;
    const errorMap = {
      rate_limit_exceeded: "操作過於頻繁，請稍後再試",
      account_locked: "帳號已被鎖定，請稍後再試",
      invalid_credentials: "帳號或密碼錯誤",
    };
    return errorMap[errorMsg] || errorMsg;
  }

  _translateRawTSMRecoverError(errorMsg) {
    const text = String(errorMsg || "");
    if (!text || !text.includes("TSM 回應錯誤")) return "";
    const isRecover = text.includes("type_mode:Recover") || text.includes("type_mode Recover");
    const isRejected = text.includes("status=-999") || text.includes("Internal Server Error");
    if (!isRecover || !isRejected) return "";
    const isNetworkDeviceAlarm = text.includes("網路設備離線") || text.includes("subtype:11") || text.includes("main_code:72");
    if (isNetworkDeviceAlarm) {
      return "TSM 主機拒絕警報復歸：設備可能仍離線，或此類告警需由主機警報復歸/警報確認流程處理。請先確認設備連線後再重試。";
    }
    return "TSM 主機拒絕警報復歸。請確認告警來源設備已恢復，或改用主機警報復歸/警報確認流程處理。";
  }

  _translateHTTPStatusFallback(status) {
    const map = {
      400: {
        title: "操作資料不完整",
        hint: "送出的資料格式不正確，請重新確認欄位後再試。",
      },
      401: {
        title: "登入或授權已失效",
        hint: "請重新登入後再試。",
      },
      444: {
        title: "登入已逾時",
        hint: "登入狀態已過期，請重新登入後再試。",
      },
      403: {
        title: "無操作權限",
        hint: "目前帳號沒有執行此操作的權限。",
      },
      404: {
        title: "找不到指定功能或資料",
        hint: "目前服務路由不存在或資料已被移除，請重新載入頁面後再試。",
      },
      409: {
        title: "資料狀態已變更",
        hint: "資料狀態與操作不一致，請重新載入頁面後再試。",
      },
      423: {
        title: "資料目前不可變更",
        hint: "目標資料已被鎖定或暫時不可操作，請稍後再試。",
      },
      429: {
        title: "操作過於頻繁",
        hint: "請稍後再試。",
      },
    };
    if (map[status]) return { icon: "warning", ...map[status] };
    if (status >= 500) {
      return {
        icon: "error",
        title: "服務暫時無法完成操作",
        hint: `系統回傳 ${status}，請稍後再試；若持續發生請查看操作紀錄或聯絡系統管理員。`,
      };
    }
    return {
      icon: "warning",
      title: "操作未完成",
      hint: `系統回傳 ${status}，請重新確認資料後再試。`,
    };
  }

  // TSM master 同步系列 7 個 Code 對應到固定 dialog 文案
  // 對齊 backend tsm_sync.go preflightChecks 與 codeMessage()
  // 物業看到 dialog 後通常需自行採取行動: 設定帳密 / 等系統商解除全域 kill switch / 重試 sync
  _translateTSMCode(code, data = {}) {
    const map = {
      TSM_PASSWORD_NOT_CONFIGURED: {
        title: "尚未設定 TSM 主機帳密",
        hint: "請至「系統設定 > TSM 主機帳密」設定後再進行同步。",
      },
      TSM_PASSWORD_CORRUPTED: {
        title: "TSM 帳密無法解密",
        hint: "可能是金鑰已輪替，請至「系統設定 > TSM 主機帳密」重新設定密碼。",
      },
      WEB_HTTP_DISABLED: {
        title: "門禁功能尚未開放",
        hint: "門禁讀取/寫入目前未開放，請聯絡您的建設公司窗口或系統商開啟後再試。",
      },
      TSM_WEB_HTTP_KILL_SWITCH: {
        title: "系統商已停用門禁同步",
        hint: "請聯絡系統商開啟門禁同步後再試。",
      },
      TSM_KILL_SWITCH_TRIGGERED_MID_FLIGHT: {
        title: "本次同步已中止",
        hint: "系統商在執行過程中暫停門禁同步，請稍後重試。",
      },
      TSM_RECOVER_REJECTED: {
        title: "TSM 主機拒絕警報復歸",
        hint: "請先確認告警來源設備已恢復；若仍失敗，請改用主機警報復歸或警報確認流程處理。",
      },
      EMPTY_TSM_SAFEGUARD: {
        title: "TSM 主機讀不到持卡人資料",
        hint: "為避免解析異常誤刪雲端資料，已暫停以主機為準的清理。請先確認主機連線與資料正常後再同步。",
      },
      CRED_DRIFT: {
        title: "TSM 密碼漂移",
        hint: "社區伺服器與雲端帳密不一致，請重新同步一次；若持續失敗請重設帳密。",
      },
      tsm_host_unreachable: {
        title: "TSM 主機無法連線",
        hint: "社區伺服器連不上門禁主機，請確認主機電源、網路線與 IP 設定後再試。",
      },
      local_server_offline: {
        title: "社區伺服器離線",
        hint: "社區端主機目前未連上雲端，請確認該主機電源與網路後再試。",
      },
      reauth_required: {
        title: "需要二次驗證",
        hint: "請重新輸入目前登入帳號的密碼後再執行此操作。",
      },
      reauth_invalid: {
        title: "二次驗證失敗",
        hint: "密碼不正確，請確認後再試。",
      },
      challenge_expired: {
        title: "驗證已逾時",
        hint: "請重新送出二次驗證。",
      },
      account_locked: {
        title: "帳號暫時鎖定",
        hint: "密碼錯誤次數過多，請稍後再試。",
      },
      reason_required: {
        title: "請填寫操作原因",
        hint: "此門禁操作需要留下操作原因，請輸入原因後再送出。",
      },
      missing_reason: {
        title: "請填寫操作原因",
        hint: "此門禁操作需要留下操作原因，請輸入原因後再送出。",
      },
      confirm_and_reason_required: {
        title: "請完成二次確認",
        hint: "請勾選確認項目並填寫操作原因後再執行。",
      },
      operation_not_executable: {
        title: "此操作不可直接執行",
        hint: "此項已改由物業平台日常功能頁處理，請改用對應頁面完成操作。",
      },
      PDF_NATIVE_REQUIRED: {
        title: "請改用平台原生流程",
        hint: "此項已有物業平台原生流程，不再提供 TSM Web 備援操作。",
      },
      method_not_allowed: {
        title: "操作方式不支援",
        hint: "此門禁功能不支援目前的操作方式，請改用頁面提供的按鈕或表單。",
      },
      invalid_request: {
        title: "操作資料不完整",
        hint: "送出的門禁操作資料不完整，請重新確認欄位後再試。",
      },
      invalid_path_param: {
        title: "操作資料不完整",
        hint: "送出的門禁操作目標不完整，請重新選擇資料後再試。",
      },
      audit_failed: {
        title: "操作紀錄建立失敗",
        hint: "系統未能建立門禁操作紀錄，操作尚未送出，請稍後再試。",
      },
      tsm_http_failed: () => {
        const statusCode = data?.StatusCode ? `（主機狀態碼 ${data.StatusCode}）` : "";
        return {
          title: "門禁主機執行失敗",
          hint: `門禁主機拒絕或未完成此次操作${statusCode}，請查看操作紀錄或稍後再試。`,
        };
      },
      TSM_BREAK_GLASS_DISABLED: {
        title: "緊急開門已停用",
        hint: "目前系統未開放緊急開門，請聯絡系統管理員確認設定。",
      },
      local_offline: {
        title: "社區伺服器未連線",
        hint: "社區伺服器目前離線，請確認本地主機服務後再試。",
      },
    };
    const result = map[code];
    return typeof result === "function" ? result() : result || null;
  }

  // _translateTSMCodeFallback 將尚未列入固定表的 TSM Code 轉成可理解的業務錯誤，避免誤顯示成網路異常。
  _translateTSMCodeFallback(code, status) {
    const normalized = String(code || "")
      .replace(/^TSM_/, "")
      .replace(/_/g, " ")
      .toLowerCase();
    const readable = normalized.replace(/\b\w/g, (char) => char.toUpperCase());
    const isServerError = Number(status || 0) >= 500;
    return {
      icon: isServerError ? "error" : "warning",
      title: isServerError ? "門禁系統暫時無法完成操作" : "門禁操作未完成",
      hint: `系統回傳 ${readable || code}，請確認操作資料後再試；若持續發生請查看操作紀錄。`,
    };
  }
}

// 自動載入並合併同資料夾下其它 API module
const context = require.context(".", true, /^\.\/(?!index).*\.js$/);
context.keys().forEach((key) => {
  const module = context(key);
  Object.assign(ApiClient.prototype, module);
});

export default ApiClient;
